import { HashrateItem } from "@components/Charts/ChartDatasetUtils";
import { CleanChartDataset } from "@components/Charts/CleanChart.control";
import { SocketContext } from "@components/Context/SocketContext";
import { Moment } from "moment-timezone";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { SocketSubTypes } from "@services/http/socket/requests";
import { getMathValues3, IMathValues3 } from "@mobx/store/locations/types";
import { IStatisticControl3 } from "./types";
import mainLocationsStore from "@mobx/store/locations";

interface IObjectStatistic3Props {
  isOpen: boolean;
  isAdmin: boolean;
}

export function useBuildingStatisticControl3(props: IObjectStatistic3Props): IStatisticControl3 {
  const buildingStore = mainLocationsStore.activeLocation?.activeBuilding;
  
  const {
    subscribe,
    unsubscribe,
  } = useContext(SocketContext);

  const [datasets, setDatasets] = useState<CleanChartDataset[]>([]);
  const [statistic, setStatistic] = useState<IMathValues3>([0, 0, 0, 0]); // min, max, avg

  const currentPeriodRef = useRef<[Moment | null, Moment | null]>([null, null]);

  useEffect(() => {
    if (props.isOpen && buildingStore?.building.id) {
      doSubscribe();
      update(currentPeriodRef.current);
      return () => {
        doUnsubscribe();
      }
    }
  }, [props.isOpen, buildingStore?.building.id]);

  const getStatisticByObject = async (
    period: [Moment | null, Moment | null],
  ): Promise<HashrateItem[] | void> => {
    const result = await buildingStore?.getStatistics(period, props.isAdmin);
    if (result) {
      const values = getMathValues3(result);
      setStatistic(values);
    }
    return result;
  }

  const update = async (period: [Moment | null, Moment | null]) => {
    if (buildingStore?.building) {
      currentPeriodRef.current = period;
      const raw = await getStatisticByObject(period);
      if (raw === undefined) return;
      const dataset: CleanChartDataset = {
        data: raw
      }
      setDatasets([dataset]);
    }
  }

  const doSubscribe = useCallback(() => {
    if (buildingStore?.building.id) {
      subscribe(SocketSubTypes.Object, buildingStore?.building.id);
    }
  }, [buildingStore?.building.id]);

  const doUnsubscribe = useCallback(() => {
    if (buildingStore?.building.id) {
      unsubscribe(SocketSubTypes.Object, buildingStore?.building.id);
    }
  }, [buildingStore?.building.id]);

  const getId = () => {
    return `obj-${buildingStore?.building.id ?? "0"}`;
  }

  const load = async () => {
    doSubscribe();
  }

  return {
    isLoading: !!buildingStore?.isLoading,
    datasets,
    statistic,
    update,
    load,
    unload: doUnsubscribe,
    getId,
  }
}